import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api } from "../api/client";
import { verifyBundle, type BundleResult } from "../crypto/bundle";
import { shortDid } from "../util";

const KIND: Record<string, string> = { claim: "Claim", vouch: "Vouch", vouch_request: "Vouch request", anchor: "Anchor", withdrawal: "Withdrawal" };

/** /c/{id}/verify (or /verify?claim=): every signature recomputed in this browser. */
export function VerifyPage() {
  const params = useParams();
  const [q] = useSearchParams();
  const id = params.id ?? q.get("claim") ?? "";
  const [res, setRes] = useState<BundleResult | null>(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.GET("/claims/{claim_id}/bundle", { params: { path: { claim_id: id } } }).then((r) => {
      if (!r.data) return setErr("No such claim.");
      try { setRes(verifyBundle(r.data)); } catch (e) { setErr((e as Error).message); }
    });
  }, [id]);
  if (err) return <p className="error">{err}</p>;
  if (!res) return <p className="note">Recomputing signatures…</p>;
  const total = res.objects.reduce((n, o) => n + o.checks.length, 0), failed = res.objects.reduce((n, o) => n + o.checks.filter((c) => !c.ok).length, 0);
  return (
    <div>
      <h1 data-testid="verify-summary" data-ok={res.ok}>{res.ok ? "Every signature recomputes" : "Something does not verify"}</h1>
      <p className="lead">{res.objects.length} signed objects, {total} checks, {failed} failed. Computed in this browser from the claim's bundle; the server's word is not used except for delegation revocation status.</p>
      {Object.entries(res.didErrors).map(([did, e]) => <p key={did} className="error">{shortDid(did)}: {e}</p>)}
      {res.objects.map((o, i) => (
        <section className="card obj" key={i} data-testid="verify-object" data-kind={o.kind} data-ok={o.ok}>
          <h3><span>{KIND[o.kind] ?? o.kind}</span><span>{o.ok ? "verified" : "FAILED"}</span></h3>
          <div className="hint mono">{o.signer ? shortDid(o.signer) : ""} {o.entryHash ? `· ${o.entryHash.slice(0, 12)}…` : ""}</div>
          {o.checks.map((c, j) => (
            <div key={j} className={`check${c.ok ? "" : " fail"}`}><span className="st">{c.ok ? "pass" : "fail"}</span><span>{c.name}{c.detail ? ` (${c.detail})` : ""}</span></div>))}
        </section>))}
      <Link className="btn" to={`/c/${id}`}>Back to the claim</Link>
    </div>
  );
}
